import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button, Form, Spinner, Alert } from 'react-bootstrap';
import StreamService from '../../../services/StreamService';

const POSITIONS = [
  ['top-left', 'Top left'],
  ['top-right', 'Top right'],
  ['bottom-left', 'Bottom left'],
  ['bottom-right', 'Bottom right'],
];

const StreamWatermark = ({ streamId }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  const flash = (variant, text) => { setMsg({ variant, text }); setTimeout(() => setMsg(null), 5000); };

  const loadPreview = useCallback(async () => {
    const url = await StreamService.fetchLogoObjectUrl(streamId);
    setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
  }, [streamId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await StreamService.getLogo(streamId);
      setCfg(r.logo);
      if (r.logo?.has_file) loadPreview();
    } catch (e) {
      flash('danger', e.response?.data?.error || 'Failed to load watermark settings');
    } finally {
      setLoading(false);
    }
  }, [streamId, loadPreview]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await StreamService.uploadLogo(streamId, file);
      flash('success', 'Logo uploaded');
      await load();
    } catch (err) {
      flash('danger', err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const patch = (changes) => setCfg((c) => ({ ...c, ...changes }));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const body = {
        enabled: cfg.enabled,
        position: cfg.position,
        scale: cfg.scale,
        margin: cfg.margin,
        opacity: cfg.opacity,
        ...extra,
      };
      await StreamService.updateLogo(streamId, body);
      flash('success', 'Saved — changes apply to the live stream within a few seconds');
    } catch (e) {
      flash('danger', e.response?.data?.error || 'Save failed');
      load();
    } finally {
      setSaving(false);
    }
  };

  const removeLogo = async () => {
    setSaving(true);
    try {
      await StreamService.removeLogo(streamId);
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
      flash('success', 'Logo removed');
      await load();
    } catch (e) {
      flash('danger', 'Failed to remove logo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
  if (!cfg) return <Alert variant="danger">Could not load watermark settings.</Alert>;

  // preview box — a 16:9 frame with the logo placed in the chosen corner
  const cornerStyle = () => {
    const m = `${Math.min(24, cfg.margin / 4)}px`;
    const base = { position: 'absolute', width: `${cfg.scale * 1.6}%`, opacity: cfg.opacity / 100 };
    if (cfg.position === 'top-left') return { ...base, top: m, left: m };
    if (cfg.position === 'top-right') return { ...base, top: m, right: m };
    if (cfg.position === 'bottom-left') return { ...base, bottom: m, left: m };
    return { ...base, bottom: m, right: m };
  };

  return (
    <>
      {msg && <Alert variant={msg.variant} className="mb-3">{msg.text}</Alert>}

      <Card className="mb-4">
        <Card.Header><strong>Stream watermark / logo</strong></Card.Header>
        <Card.Body>
          <p className="text-muted small mb-4">
            Burns your logo into the broadcast — it appears on the HLS output for every viewer, on any player.
            PNG with transparency works best. Changes take effect within a few seconds while you're live.
          </p>

          <div className="row g-4">
            {/* Left: file + toggle */}
            <div className="col-md-5">
              <div
                className="d-flex align-items-center justify-content-center mb-3"
                style={{ height: 120, border: '1px dashed var(--c-line-strong, #ccc)', borderRadius: 12, background: 'var(--c-surface-3, #f4f4f7)' }}
              >
                {previewUrl
                  ? <img src={previewUrl} alt="logo" style={{ maxHeight: 96, maxWidth: '80%' }} />
                  : <span className="text-muted small">No logo uploaded</span>}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/webp,image/jpeg" hidden onChange={onPick} />
              <div className="d-flex gap-2">
                <Button variant="outline-primary" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? 'Uploading…' : previewUrl ? 'Replace' : 'Upload logo'}
                </Button>
                {previewUrl && <Button variant="outline-danger" size="sm" onClick={removeLogo} disabled={saving}>Remove</Button>}
              </div>

              <Form.Check
                type="switch"
                id="wm-enabled"
                className="mt-4"
                label={<strong>Show watermark on this stream</strong>}
                checked={!!cfg.enabled}
                disabled={!cfg.has_file}
                onChange={(e) => { patch({ enabled: e.target.checked }); save({ enabled: e.target.checked }); }}
              />
              {!cfg.has_file && <div className="text-muted small mt-1">Upload a logo first.</div>}
            </div>

            {/* Right: placement + preview */}
            <div className="col-md-7">
              <Form.Label className="small fw-bold text-uppercase text-muted">Position</Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {POSITIONS.map(([val, label]) => (
                  <Button
                    key={val}
                    size="sm"
                    variant={cfg.position === val ? 'primary' : 'outline-secondary'}
                    onClick={() => patch({ position: val })}
                  >
                    {label}
                  </Button>
                ))}
              </div>

              <div
                style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#111', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}
              >
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: 12 }}>preview</span>
                {previewUrl && <img src={previewUrl} alt="" style={cornerStyle()} />}
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="small d-flex justify-content-between">
                  <span>Size</span><span className="text-muted">{cfg.scale}% of width</span>
                </Form.Label>
                <Form.Range min={3} max={40} value={cfg.scale} onChange={(e) => patch({ scale: Number(e.target.value) })} />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small d-flex justify-content-between">
                  <span>Margin from edge</span><span className="text-muted">{cfg.margin}px</span>
                </Form.Label>
                <Form.Range min={0} max={120} step={4} value={cfg.margin} onChange={(e) => patch({ margin: Number(e.target.value) })} />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small d-flex justify-content-between">
                  <span>Opacity</span><span className="text-muted">{cfg.opacity}%</span>
                </Form.Label>
                <Form.Range min={10} max={100} step={5} value={cfg.opacity} onChange={(e) => patch({ opacity: Number(e.target.value) })} />
              </Form.Group>

              <Button variant="primary" onClick={() => save()} disabled={saving || !cfg.has_file}>
                {saving ? 'Saving…' : 'Save watermark settings'}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default StreamWatermark;
